import { api } from "./api";
import {
  AdminUser,
  AdminUserFilters,
  AdminUserResponse,
  AdminStats,
  ParkSession,
  ConnectLatencyStats,
} from "../types/admin";
import { AuditLog, AuditLogFilters, AuditLogResponse } from "../types/audit";

export interface AdminChargeFilters {
  from?: string;
  to?: string;
  campaignId?: string;
  limit?: number;
  sync?: boolean;
}

export type AdminChargeCallType = 'ai' | 'auto' | 'manual' | 'other';

export interface AdminChargeCallTypeRow {
  callType: AdminChargeCallType;
  totalCalls: number;
  connectedCalls: number;
  chargedCalls: number;
  totalDurationSeconds: number;
  voiceCost: number;
  relayCost: number;
  llmCost: number;
  ttsCost: number;
  /** Self-hosted engine only; absent from a backend without migration 095. */
  sttCost?: number;
  totalCost: number;
  averageCostPerCall: number;
  averageCostPerMinute: number;
  averageDurationSeconds: number;
}

export interface AdminChargeResponse {
  filters: {
    from: string;
    to: string;
    campaignId: string | null;
    limit: number;
  };
  overview: {
    totalCalls: number;
    chargedCalls: number;
    missingCostCalls: number;
    totalDurationSeconds: number;
    totalCost: number;
    averageCostPerCall: number;
    averageCostPerMinute: number;
    voiceCost?: number;
    aiCost?: number;
    sharedCost?: number;
    openaiCost?: number;
    elevenlabsCost?: number;
    deepgramCost?: number;
  };
  byCallType?: {
    rows: AdminChargeCallTypeRow[];
    attributedTotalCost: number;
    attributedTotalCalls: number;
    unattributedCost: number;
    accountTotalCost: number;
    columnsReady: boolean;
  } | null;
  daily: Array<{
    day: string;
    totalCalls: number;
    chargedCalls: number;
    totalDurationSeconds: number;
    totalCost: number;
    aiCost?: number;
  }>;
  calls: Array<{
    id: string;
    campaignId: string;
    campaignName: string;
    leadId: string | null;
    leadPhone: string | null;
    status: string;
    durationSeconds: number;
    cost: number | null;
    telnyxCallControlId: string | null;
    telnyxCallSessionId: string | null;
    createdAt: string;
  }>;
  billingSource?: 'telnyx_usage_reports' | 'internal_call_attempts';
  usageReport?: {
    totalCost: number;
    totalAttempted: number;
    totalCompleted: number;
    totalCallSeconds: number;
    connectionIds?: string[];
    campaignScopeCount?: number;
  } | null;
  portalUsage?: {
    inbound: Array<{
      product: string;
      attemptedCalls: number;
      billableCalls: number;
      billedSeconds: number;
      averageCallDurationSeconds: number;
      cost: number;
    }>;
    outbound: Array<{
      product: string;
      attemptedCalls: number;
      billableCalls: number;
      billedSeconds: number;
      averageCallDurationSeconds: number;
      cost: number;
    }>;
    totals: {
      inboundCost: number;
      outboundCost: number;
      overallCost: number;
    };
    byDay?: Array<{
      day: string;
      cost: number;
      billedSeconds: number;
      attemptedCalls: number;
      billableCalls: number;
    }>;
  } | null;
  aiUsage?: {
    products: Array<{
      key: string;
      product: string;
      category: 'ai' | 'shared';
      cost: number;
    }>;
    totals: {
      aiCost: number;
      sharedCost: number;
      overallCost: number;
    };
    byDay?: Array<{ day: string; cost: number; aiCost: number }>;
    scope?: 'account';
  } | null;
  providerMetering?: {
    openai: {
      cost: number;
      promptTokens: number;
      completionTokens: number;
      meteredCalls: number;
      pricingConfigured: boolean;
    };
    elevenlabs: {
      cost: number;
      characters: number;
      meteredCalls: number;
      pricingConfigured: boolean;
    };
    /** Self-hosted engine only; absent on responses from a backend without migration 095. */
    deepgram?: {
      cost: number;
      seconds: number;
      meteredCalls: number;
      pricingConfigured: boolean;
    };
    botCalls: number;
    customAiStackCost: number;
    averageCustomAiCostPerCall?: number;
    averageOpenaiCostPerCall?: number;
    averageElevenlabsCostPerCall?: number;
    averageDeepgramCostPerCall?: number;
    columnsReady: boolean;
  } | null;
}

export interface AdminChargeSyncResponse {
  attemptsConsidered: number;
  cdrsFetched: number;
  updated: number;
  unmatched: number;
  aiUpdated?: number;
  truncated?: boolean;
  from: string;
  to: string;
  usageReport?: {
    totalCost: number;
    totalAttempted: number;
    totalCompleted: number;
    totalCallSeconds: number;
  } | null;
  portalUsage?: {
    inbound: Array<{
      product: string;
      attemptedCalls: number;
      billableCalls: number;
      billedSeconds: number;
      averageCallDurationSeconds: number;
      cost: number;
    }>;
    outbound: Array<{
      product: string;
      attemptedCalls: number;
      billableCalls: number;
      billedSeconds: number;
      averageCallDurationSeconds: number;
      cost: number;
    }>;
    totals: {
      inboundCost: number;
      outboundCost: number;
      overallCost: number;
    };
    byDay?: Array<{
      day: string;
      cost: number;
      billedSeconds: number;
      attemptedCalls: number;
      billableCalls: number;
    }>;
  } | null;
  note?: string;
}

export interface AreaCodeTimezoneMapping {
  area_code: string;
  timezone: 'Eastern' | 'Central' | 'Mountain' | 'Pacific';
  source: string;
  updated_at: string;
}

/**
 * The connect report: what the cancelled-call number is made of, where the
 * connect time goes, and how many live merchants were dropped while an agent
 * sat available. Human auto-dial only — nothing else is traced.
 */
export interface ConnectReportReason {
  reason: string;
  count: number;
  /** Share of the window's traced calls, 0–1. */
  share: number;
}

export interface ConnectReportStage {
  stage: string;
  samples: number;
  p50Ms: number | null;
  p95Ms: number | null;
}

export interface ConnectReport {
  range: { from: string; to: string };
  breakdown: {
    totalTraced: number;
    totalCancelled: number;
    reasons: ConnectReportReason[];
  };
  latency: ConnectReportStage[];
  lostWithAgentAvailable: {
    count: number;
    noAgentTotal: number;
    callAttemptIds: string[];
  };
  unreachableAgentDrops: {
    count: number;
    noAgentTotal: number;
    callAttemptIds: string[];
  };
  nobodyThere: {
    amdFalsePositives: number;
    merchantHungUpBeforeBridge: number;
    connectedTotal: number;
    awaitingAgentTag: number;
  };
}

/** One row of `call_connect_traces`, as the API returns it. */
export interface ConnectTrace {
  call_attempt_id: string;
  dial_started_at: string | null;
  merchant_answered_at: string | null;
  agent_lookup_at: string | null;
  agent_reserved_at: string | null;
  bridge_requested_at: string | null;
  bridge_completed_at: string | null;
  /**
   * First moment the two parties could actually hear each other. Later than
   * `bridge_completed_at`, which is only the agent picking up — the gap between
   * them is silence the merchant hears.
   */
  audio_ready_at: string | null;
  bridge_failed_at: string | null;
  chosen_agent_id: string | null;
  retry_count: number | null;
  final_reason: string | null;
  final_reason_raw: string | null;
  agent_disposition: string | null;
  reject_reasons: Record<string, string> | null;
  candidates: Array<{
    agentId: string;
    state: string;
    reason: string;
    rtcReadiness?: string;
  }> | null;
}

export const adminService = {
  // User management
  async getUsers(filters?: AdminUserFilters): Promise<AdminUserResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(`/admin/users?${params.toString()}`);
    return response.data;
  },

  async getUser(id: string): Promise<AdminUser> {
    const response = await api.get(`/admin/users/${id}`);
    // Backend typically returns { success, data: { user } }
    return response.data?.data?.user ?? response.data?.data ?? response.data;
  },

  async updateUser(
    id: string,
    data: Partial<AdminUser> & {
      emailVerified?: boolean;
      role?: 'admin' | 'user' | 'agent';
      phoneNumberId?: string | null;
      phoneNumber?: string | null;
      phoneNumberProvider?: string | null;
    }
  ): Promise<AdminUser> {
    const response = await api.patch(`/admin/users/${id}`, data);
    // Backend returns { success, message, data: { user } }
    return response.data?.data?.user ?? response.data?.data ?? response.data;
  },

  async createUser(
    data: {
      name: string;
      email: string;
      password: string;
      emailVerified?: boolean;
      role?: 'admin' | 'user' | 'agent';
      phoneNumberId?: string;
      phoneNumber?: string;
      phoneNumberProvider?: string;
    }
  ): Promise<AdminUser> {
    const response = await api.post(`/admin/users`, data);
    return response.data?.data ?? response.data; // backend returns { success, data }
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/admin/users/${id}`);
  },

  async resetUserPassword(id: string, password: string): Promise<void> {
    await api.post(`/admin/users/${id}/reset-password`, { password });
  },

  async suspendUser(id: string): Promise<void> {
    await api.post(`/admin/users/${id}/suspend`);
  },

  async unsuspendUser(id: string): Promise<void> {
    await api.post(`/admin/users/${id}/unsuspend`);
  },

  // Audit logs
  async getAuditLogs(filters?: AuditLogFilters): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(`/admin/audit-logs?${params.toString()}`);
    return response.data;
  },

  async getAuditLog(id: string): Promise<AuditLog> {
    const response = await api.get(`/admin/audit-logs/${id}`);
    return response.data;
  },

  // Stats
  async getStats(): Promise<AdminStats> {
    const response = await api.get("/admin/stats");
    return response.data;
  },

  // System actions
  async clearCache(): Promise<void> {
    await api.post("/admin/cache/clear");
  },

  async runMaintenance(): Promise<void> {
    await api.post("/admin/maintenance/run");
  },

  async exportData(type: "users" | "audit-logs", filters?: any): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(
      `/admin/export/${type}?${params.toString()}`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  },

  async getCharges(filters?: AdminChargeFilters): Promise<AdminChargeResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    const query = params.toString();
    const response = await api.get(`/admin/charges${query ? `?${query}` : ''}`);
    return response.data?.data ?? response.data;
  },

  async syncCharges(payload: {
    from?: string;
    to?: string;
    campaignId?: string;
    maxPages?: number;
  }): Promise<AdminChargeSyncResponse> {
    const response = await api.post('/admin/charges/sync', payload);
    return response.data?.data ?? response.data;
  },

  async getAreaCodeTimezones(params?: { search?: string; page?: number; limit?: number }): Promise<{
    mappings: AreaCodeTimezoneMapping[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    const response = await api.get(`/admin/area-code-timezones${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data?.data ?? response.data;
  },

  async upsertAreaCodeTimezone(areaCode: string, timezone: 'Eastern' | 'Central' | 'Mountain' | 'Pacific'): Promise<void> {
    await api.post('/admin/area-code-timezones', { areaCode, timezone });
  },

  async bulkUpsertAreaCodeTimezones(mappings: Array<{ areaCode: string; timezone: 'Eastern' | 'Central' | 'Mountain' | 'Pacific' }>): Promise<{ imported: number }> {
    const response = await api.post('/admin/area-code-timezones/bulk', { mappings });
    return response.data?.data ?? response.data;
  },

  async deleteAreaCodeTimezone(areaCode: string): Promise<void> {
    await api.delete(`/admin/area-code-timezones/${areaCode}`);
  },

  /** Turn the instant-connect parked leg on or off for one agent. */
  async setAgentParkEnabled(agentId: string, enabled: boolean): Promise<boolean> {
    const response = await api.put(`/admin/agents/${agentId}/park`, { enabled });
    return response.data?.data?.parkEnabled ?? enabled;
  },

  /** Same, for everyone on a campaign — the unit a rollout is run in. */
  async setCampaignParkEnabled(campaignId: string, enabled: boolean): Promise<number> {
    const response = await api.put(`/admin/campaigns/${campaignId}/park`, { enabled });
    return response.data?.data?.updated ?? 0;
  },

  /** What every parked seat is doing right now. Empty when park is switched off. */
  async getParkSessions(): Promise<ParkSession[]> {
    const response = await api.get('/admin/park-sessions');
    return response.data?.data?.sessions ?? [];
  },

  /**
   * Drop one agent's parked leg by hand. Park stays enabled — their browser
   * re-parks on the next heartbeat, so this is a reset, not an opt-out.
   */
  async unparkAgent(agentId: string): Promise<void> {
    await api.post(`/admin/agents/${agentId}/unpark`);
  },

  /**
   * The connect report over a window. Everything the auto-dialer records about
   * why calls end and where the connect time goes, in one call.
   */
  async getConnectReport(params: {
    from: string;
    to: string;
    campaignId?: string;
  }): Promise<ConnectReport> {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.campaignId) query.set('campaignId', params.campaignId);
    const response = await api.get(`/admin/connect-report?${query.toString()}`);
    return response.data.data;
  },

  /** Answer-to-audio for the human auto-dialer. Rolling window of recent calls. */
  async getConnectLatency(campaignId?: string): Promise<ConnectLatencyStats> {
    const query = campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : '';
    const response = await api.get(`/admin/connect-latency${query}`);
    const data = response.data?.data;
    return {
      campaignId: data?.campaignId ?? campaignId ?? null,
      count: data?.count ?? 0,
      p50: data?.p50 ?? null,
      p95: data?.p95 ?? null,
      min: data?.min ?? null,
      max: data?.max ?? null,
    };
  },

  async backfillLeadsAreaCode(): Promise<{ areaCodeFilled: number }> {
    const response = await api.post('/admin/area-code-timezones/backfill-leads');
    return response.data?.data ?? response.data;
  },

  async getSmsDashboardEvents(params: {
    campaignId?: string;
    startDate?: string;
    endDate?: string;
    allTime?: boolean;
    period?: 'today' | 'all' | 'range';
    page?: number;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    if (params.campaignId) query.set('campaignId', params.campaignId);
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.allTime) query.set('allTime', 'true');
    if (params.period) query.set('period', params.period);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const response = await api.get(`/admin/sms-dashboard/events?${query.toString()}`);
    return response.data?.data ?? response.data;
  },

  async getTelnyxBotReadiness(): Promise<{
    hasApiKey: boolean;
    hasPlatformAssistantId: boolean;
    hasPublicBaseUrl: boolean;
    hasWebhookPublicKey: boolean;
    hasLlmApiKeyRef: boolean;
    hasInsightGroupId: boolean;
    assistantVariablesWebhookUrl: string | null;
    callWebhookUrl: string | null;
  }> {
    const response = await api.get('/admin/telnyx/bot-readiness');
    return response.data.data;
  },

  async syncTelnyxPlatformAssistant(assistantId?: string): Promise<{
    assistantId: string;
    webhookUrl: string;
  }> {
    const response = await api.post('/admin/telnyx/assistants/sync-platform', {
      ...(assistantId ? { assistantId } : {}),
    });
    return response.data.data;
  },

  /**
   * Connect trace for one call — the stamps between dial and agent audio, and
   * the agent pool as it stood when routing decided.
   *
   * Admin-only, and lives on adminService rather than callService for that
   * reason: the trace carries a snapshot of every agent's availability, lock
   * and WebRTC state, which is not an agent's business to read.
   *
   * Returns null for a call with no trace — bot, AI and manual dials are never
   * traced, and neither is anything dialled before the feature shipped. That is
   * a normal state, not an error.
   */
  async getConnectTrace(callAttemptId: string): Promise<ConnectTrace | null> {
    try {
      const response = await api.get(`/admin/connect-report/trace/${callAttemptId}`);
      return response.data.data ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 400) return null;
      throw err;
    }
  },
};
